import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/api/client";
import { Search, ArrowRight, LayoutDashboard, Inbox, List, Calendar, BarChart3, Sun, Pause, Activity, Archive, Settings } from "lucide-react";
import { stageColor } from "@/lib/palette";
import { deriveInitials } from "@/lib/pipeline";

const NAV_LINKS = [
  { to: "/", label: "Board", icon: LayoutDashboard },
  { to: "/inbox", label: "Inbox", icon: Inbox },
  { to: "/clients", label: "Clients", icon: List },
  { to: "/calendar", label: "Calendar", icon: Calendar },
  { to: "/my-day", label: "My Day", icon: Sun },
  { to: "/archived", label: "Archived", icon: Archive },
  { to: "/stuck", label: "Stuck", icon: Pause },
  { to: "/activity", label: "Activity", icon: Activity },
  { to: "/reports", label: "Reports", icon: BarChart3 },
  { to: "/settings", label: "Settings", icon: Settings },
];

export default function CommandPalette() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [cards, setCards] = useState([]);
  const [active, setActive] = useState(0);
  const nav = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQ("");
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 50);
      if (cards.length === 0) {
        api.get("/cards", { params: { status: "active" } }).then(({ data }) => setCards(data));
      }
    }
  }, [open]); // eslint-disable-line

  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    const navHits = s
      ? NAV_LINKS.filter((n) => n.label.toLowerCase().includes(s)).map((n) => ({ kind: "nav", ...n }))
      : NAV_LINKS.map((n) => ({ kind: "nav", ...n })).slice(0, 4);
    if (!s) return navHits;
    const cardHits = cards
      .filter((c) => `${c.full_name} ${c.av_id} ${c.payer_mco}`.toLowerCase().includes(s))
      .slice(0, 12)
      .map((c) => ({ kind: "card", card: c }));
    return [...navHits, ...cardHits];
  }, [q, cards]);

  const go = (item) => {
    if (item.kind === "nav") nav(item.to);
    else nav(`/clients`); // opens clients list — actual card detail open requires panel; keep simple
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && results[active]) { go(results[active]); }
  };

  if (!open) return null;
  if (user?.role === "field_agent") return null;
  return (
    <div
      className="fixed inset-0 z-[80] bg-[#15233B]/60 backdrop-blur-sm flex items-start justify-center pt-24 p-4"
      data-testid="command-palette"
      onClick={() => setOpen(false)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl bg-white rounded-xl shadow-2xl ring-1 ring-[#15233B]/10 overflow-hidden"
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[#15233B]/10 bg-[#F2F2F2]">
          <Search className="h-4 w-4 text-[#15233B]/55" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => { setQ(e.target.value); setActive(0); }}
            onKeyDown={onKeyDown}
            placeholder="Search clients, AV-IDs, pages…"
            data-testid="command-input"
            className="flex-1 bg-transparent outline-none text-sm text-[#15233B] placeholder:text-[#15233B]/40"
          />
          <kbd className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-white border border-[#15233B]/15 text-[#15233B]/55">Esc</kbd>
        </div>
        <ul className="max-h-80 overflow-y-auto py-2">
          {results.length === 0 && (
            <li className="px-4 py-6 text-sm italic text-[#15233B]/40 text-center">No matches.</li>
          )}
          {results.map((r, i) => {
            const isActive = i === active;
            if (r.kind === "nav") {
              const I = r.icon;
              return (
                <li key={`nav-${r.to}`}>
                  <button
                    onClick={() => go(r)}
                    onMouseEnter={() => setActive(i)}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-sm text-left ${isActive ? "bg-[#FFCB05]/15" : "hover:bg-[#F2F2F2]"}`}
                  >
                    <I className="h-4 w-4 text-[#1E73C9]" />
                    <span className="flex-1 font-semibold text-[#15233B]">{r.label}</span>
                    <ArrowRight className="h-3 w-3 text-[#15233B]/40" />
                  </button>
                </li>
              );
            }
            const c = r.card;
            const sc = stageColor(c.current_stage);
            return (
              <li key={`card-${c.id}`}>
                <button
                  onClick={() => go(r)}
                  onMouseEnter={() => setActive(i)}
                  className={`w-full flex items-center gap-3 px-4 py-2 text-sm text-left ${isActive ? "bg-[#FFCB05]/15" : "hover:bg-[#F2F2F2]"}`}
                >
                  <div className="h-7 w-7 rounded-full grid place-items-center text-[10px] font-bold shrink-0" style={{ backgroundColor: sc.primary, color: sc.text }}>
                    {deriveInitials(c.full_name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[#15233B] truncate">{c.full_name}</div>
                    <div className="text-[11px] text-[#15233B]/55 truncate font-mono">{c.av_id} · Stage {c.current_stage} · {c.payer_mco}</div>
                  </div>
                  <ArrowRight className="h-3 w-3 text-[#15233B]/40" />
                </button>
              </li>
            );
          })}
        </ul>
        <div className="px-4 py-2 border-t border-[#15233B]/10 bg-[#F2F2F2] flex items-center gap-3 text-[10px] text-[#15233B]/55">
          <span><kbd className="font-mono font-bold">↑↓</kbd> navigate</span>
          <span><kbd className="font-mono font-bold">↵</kbd> select</span>
          <span className="ml-auto">⌘K / Ctrl K to toggle</span>
        </div>
      </div>
    </div>
  );
}
